import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import {
  Badge,
  EmptyState,
  Button,
  Input,
  Pagination,
  SectionHeader,
  ViewToggle,
  SwitchTrack,
  Textarea,
} from "../../../components/ui";
import { useRoutesList } from "../../../features/routes/queries";
import type { Route } from "../../../store/admin/routesStore";
import { useRoutesStore } from "../../../store/admin/routesStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useAlert } from "../../../context/AlertContext";
import { useDarkMode } from "../../../context/ThemeContext";
import { panelThemeClasses } from "../../../theme/themeClasses";
import { qk } from "../../../lib/queryKeys";
import {
  RouteModal,
  useRouteForm,
  type RouteFormValues,
} from "../../admin/components/sections/routesComponents";
import FilterCombobox from "../components/FilterCombobox";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import ResponsiveRow, { ResponsiveHeader } from "../components/ResponsiveRow";
import { useCentinelaDemo } from "../services/centinelaDemoMode";
import {
  buildAiRouteDraft,
  buildDemoRoutes,
  buildRouteInsights,
  isDemoRecord,
} from "../services/centinelaMocks";
import { ROUTE_STOP_DOTS } from "../theme";
import type { RouteInsight } from "../types";

const ITEMS_PER_PAGE = 6;
const ROUTE_GRID = "lg:grid-cols-[1fr_160px_120px_90px_90px_60px]";
const STATUS_FILTERS = ["all", "active", "inactive"] as const;
const SOURCE_FILTERS = ["all", "ai", "manual"] as const;
const RISK_FILTERS = ["all", "low", "medium", "high"] as const;

const riskKeyOf = (score: number) => {
  if (score >= 70) return "high" as const;
  if (score >= 45) return "medium" as const;
  return "low" as const;
};

const RISK_CHIPS = {
  low: "border-success/30 bg-success/10 text-success",
  medium: "border-warning/30 bg-warning/10 text-warning",
  high: "border-error/30 bg-error/10 text-error",
} as const;

const RISK_DOTS = {
  low: "bg-success",
  medium: "bg-warning",
  high: "bg-error",
} as const;

const safetyTone = (score: number) => {
  if (score >= 80) return "text-success";
  if (score >= 50) return "text-warning";
  return "text-error";
};

const timestampToDate = (value: unknown) => {
  if (value && typeof value === "object" && "_seconds" in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  return null;
};

export default function RutasSection() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Route | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editRoute, setEditRoute] = useState<Route | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});

  const companyId = useCompaniesStore((state) => state.companyId);
  const { createRoute, updateRoute } = useRoutesStore();
  const { data, isLoading, isFetching, refetch } = useRoutesList({ limit: 200 });

  const forceDemo = useCentinelaDemo();

  const themeClasses = panelThemeClasses();

  // Route form hook
  const {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  } = useRouteForm({
    isEdit,
    editRoute,
  });

  const invalidateRoutes = () => {
    queryClient.invalidateQueries({ queryKey: ["routes"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const routes = useMemo(() => {
    const rows = forceDemo ? buildDemoRoutes(t) : (data?.data ?? []);
    return rows.filter((route) => !deletedIds.includes(route.id));
  }, [data, forceDemo, t, deletedIds]);

  const insights = useMemo(() => {
    const generated = buildRouteInsights(t, routes.map((route) => route.id));
    return generated;
  }, [t, routes]);

  const insightOf = (routeId: string): RouteInsight | undefined =>
    insights.find((item) => item.routeId === routeId);

  const isActive = (route: Route) => statusOverrides[route.id] ?? route.status_id !== "inactive";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return routes.filter((route) => {
      const insight = insightOf(route.id);
      const matchesTerm =
        term.length === 0 ||
        route.name.toLowerCase().includes(term) ||
        (route.description ?? "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" ? isActive(route) : !isActive(route));
      const matchesSource =
        sourceFilter === "all" ||
        (sourceFilter === "ai" ? Boolean(insight?.createdByAi) : !insight?.createdByAi);
      const matchesRisk =
        riskFilter === "all" || (insight ? riskKeyOf(insight.riskScore) === riskFilter : false);

      return matchesTerm && matchesStatus && matchesSource && matchesRisk;
    });
  }, [routes, insights, search, statusFilter, sourceFilter, riskFilter, statusOverrides]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter, riskFilter, viewMode]);

  const totalPages = Math.max(Math.ceil(filtered.length / ITEMS_PER_PAGE), 1);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const distanceLabel = (meters: number) =>
    `${(meters / 1000).toLocaleString(i18n.language, { maximumFractionDigits: 1 })} km`;

  const durationLabel = (minutes: number) =>
    t("monitoreoIa.routes.durationValue", {
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60,
    });

  const dateOf = (value: unknown) => {
    const date = timestampToDate(value);
    return date ? date.toLocaleDateString(i18n.language, { dateStyle: "medium" }) : "—";
  };

  const toggleStatus = (route: Route) =>
    setStatusOverrides((prev) => ({ ...prev, [route.id]: !isActive(route) }));

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditRoute(null);
    setLoadingSubmit(false);
    resetForm();
  };

  const handleAddNew = () => {
    setShowAddModal(true);
  };

  const handleCreateRouteSubmit = async (data: RouteFormValues) => {
    setLoadingSubmit(true);
    const routeData = {
      ...prepareRouteData(data),
      company_id: companyId || "",
    };

    const result = await createRoute(routeData);

    if (result.success) {
      showAlert({
        title: "Éxito",
        text: "Ruta creada correctamente",
        icon: "success",
      });
      handleCloseAddModal();
      invalidateRoutes();
    } else {
      showAlert({
        title: "Error",
        text: result.error || "Error al crear la ruta",
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleUpdateRouteSubmit = async (data: RouteFormValues) => {
    if (!editRoute) return;
    setLoadingSubmit(true);

    const routeData = prepareRouteData(data);

    const result = await updateRoute(editRoute.id, routeData);

    if (result.success) {
      showAlert({
        title: "Éxito",
        text: "Ruta actualizada correctamente",
        icon: "success",
      });
      handleCloseAddModal();
      invalidateRoutes();
    } else {
      showAlert({
        title: "Error",
        text: result.error || "Error al actualizar la ruta",
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleSubmit = isEdit
    ? handleUpdateRouteSubmit
    : handleCreateRouteSubmit;

  const deleteRoute = (route: Route) => {
    setDeletedIds((prev) => [...prev, route.id]);
    setDeleteTarget(null);
    toast.success(t("monitoreoIa.routes.deleted", { name: route.name }));
  };

  const analyzeRoute = () => {
    if (draft.trim().length === 0) return;
    setIsAnalyzing(true);
    setTimeout(() => {
      const generated = buildAiRouteDraft(t, draft);
      setDraft("");
      setPage(1);
      setIsAnalyzing(false);
      toast.success(t("monitoreoIa.routes.analysisReady", { name: generated.route.name }));
    }, 1800);
  };

  if (!companyId) {
    return (
      <div className="flex h-full w-full flex-col">
        <SectionHeader
          title={t("monitoreoIa.routes.title")}
          subtitle={t("monitoreoIa.routes.subtitle", { count: 0 })}
        />
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="mx-auto w-full max-w-6xl">
            <EmptyState
              icon="ri-building-line"
              title={t("monitoreoIa.selectCompany.title")}
              description={t("monitoreoIa.selectCompany.description")}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <TabLoader label={t("monitoreoIa.routes.loadingTab")} />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.routes.title")}
        subtitle={t("monitoreoIa.routes.subtitle", { count: routes.length })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshSectionButton onRefresh={() => void refetch()} loading={isFetching} />
            <Button
              size="sm"
              icon={<i className="ri-add-line" />}
              onClick={handleAddNew}
            >
              {t("monitoreoIa.routes.new")}
            </Button>
          </div>
        }
        filters={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:gap-2.5">
            <FilterCombobox
              id="rutas-filter-1"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.routes.statusFilters.${value}`),
              }))}
              containerClassName="w-full sm:w-44"
            />
            <FilterCombobox
              id="rutas-filter-2"
              value={sourceFilter}
              onChange={setSourceFilter}
              options={SOURCE_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.routes.sourceFilters.${value}`),
              }))}
              containerClassName="w-full sm:w-48"
            />
            <FilterCombobox
              id="rutas-filter-3"
              value={riskFilter}
              onChange={setRiskFilter}
              options={RISK_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.routes.riskFilters.${value}`),
              }))}
              containerClassName="w-full max-sm:col-span-2 sm:w-44"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("monitoreoIa.routes.searchPlaceholder")}
              icon={<i className="ri-search-line" />}
              containerClassName="w-full flex-1 max-sm:order-first max-sm:col-span-2"
            />
            <ViewToggle
              value={viewMode}
              onChange={setViewMode}
              className="max-sm:col-span-2"
              labels={{
                grid: t("monitoreoIa.geofences.view.grid"),
                list: t("monitoreoIa.geofences.view.list"),
              }}
            />
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-5">
          <section className="rounded-2xl border border-dashed border-primary-500/50 bg-primary-500/5 p-5">
            <div className="mb-3 flex items-center gap-2">
              <i className="ri-sparkling-line text-primary-500" />
              <h2 className="text-[13px] font-bold text-content">
                {t("monitoreoIa.routes.assistantTitle")}
              </h2>
            </div>

            <p className="mb-4 text-xs text-content-secondary">
              {t("monitoreoIa.routes.assistantDescription")}
            </p>

            <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[11px] text-content-faint">
              {(["describe", "analyze", "plan"] as const).map((step, index) => (
                <span key={step} className="flex items-center gap-1.5">
                  {index > 0 && <i className="ri-arrow-right-s-line text-xs" />}
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-500/10 text-[10px] font-bold text-primary-500">
                    {index + 1}
                  </span>
                  {t(`monitoreoIa.routes.assistantSteps.${step}`)}
                </span>
              ))}
            </div>

            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={2}
              placeholder={t("monitoreoIa.routes.assistantPlaceholder")}
              containerClassName="mb-3"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={isAnalyzing || draft.trim().length === 0}
                onClick={analyzeRoute}
                icon={
                  isAnalyzing ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-sparkling-line" />
                  )
                }
              >
                {isAnalyzing
                  ? t("monitoreoIa.routes.analyzing")
                  : t("monitoreoIa.routes.analyzeRoute")}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                icon={<i className="ri-map-pin-line" />}
                onClick={handleAddNew}
              >
                {t("monitoreoIa.routes.manual")}
              </Button>

              <Button
                variant="link"
                size="xs"
                onClick={() => setDraft(t("monitoreoIa.routes.assistantExample"))}
              >
                {t("monitoreoIa.routes.useExample")}
              </Button>
            </div>
          </section>

          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-route-line"
              title={t("monitoreoIa.routes.empty")}
              description={t("monitoreoIa.routes.emptyDescription")}
            />
          ) : viewMode === "grid" ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paginated.map((route) => {
                  const insight = insightOf(route.id);
                  const riskKey = riskKeyOf(insight?.riskScore ?? 0);
                  const active = isActive(route);

                  return (
                    <div
                      key={route.id}
                      className={`overflow-hidden rounded-2xl border border-line bg-card transition-colors hover:border-primary-500/40 ${
                        active ? "" : "opacity-60"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 border-b border-line-soft px-4 pb-3 pt-4">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-bold text-content">
                            {route.name}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-content-faint">
                            {insight
                              ? `${insight.stops[0]?.name} → ${insight.stops[insight.stops.length - 1]?.name}`
                              : (route.description ?? "")}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => toggleStatus(route)}
                          title={t(
                            active ? "monitoreoIa.routes.deactivate" : "monitoreoIa.routes.activate",
                          )}
                          className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                            active
                              ? "border-success/30 bg-success/10 text-success"
                              : "border-line bg-subtle text-content-muted"
                          }`}
                        >
                          {t(`monitoreoIa.routes.statusFilters.${active ? "active" : "inactive"}`)}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-1.5 border-b border-line-soft px-4 py-2.5">
                        {insight?.createdByAi && (
                          <span className="flex items-center gap-1 rounded-full border border-primary-500/30 bg-primary-500/10 px-2.5 py-0.5 text-[10.5px] font-medium text-primary-500">
                            <i className="ri-sparkling-line text-[10px]" />
                            {t("monitoreoIa.routes.createdByAi")}
                          </span>
                        )}
                        <span
                          className={`rounded-full border px-2.5 py-0.5 text-[10.5px] font-medium ${RISK_CHIPS[riskKey]}`}
                        >
                          <span
                            className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${RISK_DOTS[riskKey]}`}
                          />
                          {t(`monitoreoIa.routes.risk.${riskKey}`)}
                        </span>
                        <span className="rounded-full border border-line bg-subtle px-2.5 py-0.5 text-[10.5px] font-medium text-content-secondary">
                          {distanceLabel(route.principal_route_distance ?? 0)}
                        </span>
                        <span className="rounded-full border border-line bg-subtle px-2.5 py-0.5 text-[10.5px] font-medium text-content-secondary">
                          {durationLabel(insight?.durationMin ?? 0)}
                        </span>
                        {isDemoRecord(route.id) && (
                          <Badge tone="neutral" size="sm">
                            {t("monitoreoIa.demo.badge")}
                          </Badge>
                        )}
                      </div>

                      {insight && (
                        <div className="flex flex-wrap items-center gap-1 border-b border-line-soft px-4 py-3 text-[11px]">
                          {insight.stops.map((stop, index) => (
                            <span key={`${stop.name}-${index}`} className="flex items-center gap-1">
                              <span className={`h-2 w-2 rounded-full ${ROUTE_STOP_DOTS[stop.kind]}`} />
                              <span className="font-medium text-content-secondary">{stop.name}</span>
                              {index < insight.stops.length - 1 && (
                                <i className="ri-arrow-right-line text-[10px] text-content-faint" />
                              )}
                            </span>
                          ))}
                        </div>
                      )}

                      <dl className="space-y-2 border-b border-line-soft px-4 py-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.routes.safety")}
                          </dt>
                          <dd className={`font-bold ${safetyTone(insight?.safetyScore ?? 0)}`}>
                            {insight?.safetyScore ?? 0}/100
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.routes.verification")}
                          </dt>
                          <dd className="font-semibold text-content">
                            {t("monitoreoIa.routes.everyMinutes", {
                              minutes: insight?.verificationIntervalMin ?? 0,
                            })}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.routes.unsafeZones")}
                          </dt>
                          <dd
                            className={`font-semibold ${
                              (insight?.unsafeZones ?? 0) > 0 ? "text-error" : "text-success"
                            }`}
                          >
                            {insight?.unsafeZones ?? 0}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-content-secondary">
                            {t("monitoreoIa.routes.departureWindow")}
                          </dt>
                          <dd className="font-semibold text-content">
                            {insight?.departureWindow}
                          </dd>
                        </div>
                      </dl>

                      {insight && insight.findings.length > 0 && (
                        <ul className="space-y-1.5 border-b border-line-soft px-4 py-3">
                          {insight.findings.map((finding) => (
                            <li
                              key={finding}
                              className="flex items-start gap-2 text-[11.5px] text-content-secondary"
                            >
                              <i className="ri-information-line mt-0.5 text-[11px] text-primary-500" />
                              {finding}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="flex items-center justify-between gap-2 px-4 py-3">
                        <span className="text-[11px] text-content-faint">
                          {t("monitoreoIa.routes.usedInTrips", {
                            count: insight?.usedInTrips ?? 0,
                          })}
                        </span>

                        <div className="flex shrink-0 gap-2">
                          <Button
                            size="xs"
                            onClick={() => navigate("/monitoreo-ia/viajes")}
                          >
                            {t("monitoreoIa.routes.createTrip")}
                          </Button>
                          <Button
                            variant="danger"
                            size="xs"
                            iconOnly
                            aria-label={t("monitoreoIa.actionsCommon.delete")}
                            onClick={() => setDeleteTarget(route)}
                            icon={<i className="ri-delete-bin-line" />}
                          />
                        </div>
                      </div>

                      <p className="px-4 pb-4 text-[11px] text-content-faint">
                        {t("monitoreoIa.routes.createdAt", { date: dateOf(route.createdAt) })}
                      </p>
                    </div>
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-line bg-card">
                <ResponsiveHeader
                  gridClassName={ROUTE_GRID}
                  columns={[
                    { id: "route", label: t("monitoreoIa.routes.columns.route") },
                    { id: "path", label: t("monitoreoIa.routes.columns.path") },
                    { id: "risk", label: t("monitoreoIa.routes.columns.risk") },
                    { id: "distance", label: t("monitoreoIa.routes.columns.distance"), center: true },
                    { id: "safety", label: t("monitoreoIa.routes.columns.safety"), center: true },
                    { id: "status", label: t("monitoreoIa.routes.columns.status"), center: true },
                  ]}
                />

                {paginated.map((route) => {
                  const insight = insightOf(route.id);
                  const riskKey = riskKeyOf(insight?.riskScore ?? 0);
                  const active = isActive(route);

                  return (
                    <ResponsiveRow
                      key={route.id}
                      gridClassName={ROUTE_GRID}
                      className={active ? "hover:bg-subtle" : "opacity-60"}
                      primary={
                        <>
                          <p className="truncate text-[13px] font-semibold text-content">
                            {route.name}
                          </p>
                          <p className="truncate text-[10px] text-content-faint">
                            {t(
                              insight?.createdByAi
                                ? "monitoreoIa.routes.createdByAi"
                                : "monitoreoIa.routes.createdManually",
                            )}{" "}
                            · {t("monitoreoIa.routes.usedInTrips", { count: insight?.usedInTrips ?? 0 })}
                          </p>
                        </>
                      }
                      cells={[
                        {
                          id: "path",
                          label: t("monitoreoIa.routes.columns.path"),
                          node: (
                            <span className="block truncate text-[11px] text-content-secondary">
                              {insight
                                ? `${insight.stops[0]?.name} → ${insight.stops[insight.stops.length - 1]?.name}`
                                : (route.description ?? "")}
                            </span>
                          ),
                        },
                        {
                          id: "risk",
                          label: t("monitoreoIa.routes.columns.risk"),
                          node: (
                            <span
                              className={`flex w-fit items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium ${RISK_CHIPS[riskKey]}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${RISK_DOTS[riskKey]}`} />
                              {t(`monitoreoIa.routes.risk.${riskKey}`)}
                            </span>
                          ),
                        },
                        {
                          id: "distance",
                          label: t("monitoreoIa.routes.columns.distance"),
                          center: true,
                          node: (
                            <span className="text-xs font-bold text-content">
                              {distanceLabel(route.principal_route_distance ?? 0)}
                            </span>
                          ),
                        },
                        {
                          id: "safety",
                          label: t("monitoreoIa.routes.columns.safety"),
                          center: true,
                          node: (
                            <span
                              className={`text-xs font-bold ${safetyTone(insight?.safetyScore ?? 0)}`}
                            >
                              {insight?.safetyScore ?? 0}
                            </span>
                          ),
                        },
                        {
                          id: "status",
                          label: t("monitoreoIa.routes.columns.status"),
                          center: true,
                          node: (
                            <button
                              type="button"
                              onClick={() => toggleStatus(route)}
                              title={t(
                                active
                                  ? "monitoreoIa.routes.deactivate"
                                  : "monitoreoIa.routes.activate",
                              )}
                              className="rounded-full"
                            >
                              <SwitchTrack checked={active} size="sm" />
                            </button>
                          ),
                        },
                      ]}
                    />
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>

      <RouteModal
        isOpen={showAddModal}
        isEdit={isEdit}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        routePoints={routePoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        hasRealChanges={hasRealChanges}
        onCoordinateEdit={handleCoordinateEdit}
        onAddressEdit={handleAddressEdit}
        onAddPoint={handleAddPoint}
        onRemovePoint={handleRemovePoint}
        onMapPointsChange={handleMapPointsChange}
        onRouteCalculated={handleRouteCalculated}
        onAxisCalculated={handleAxisCalculated}
        onCorridorBufferChange={handleBufferChange}
        onSubmit={handleSubmit}
        onClose={handleCloseAddModal}
      />

      {deleteTarget ? (
        <ConfirmDeleteModal
          title={t("monitoreoIa.routes.deleteTitle")}
          name={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteRoute(deleteTarget)}
        />
      ) : null}
    </div>
  );
}
